/**
 * lexisNexisReportsApi — a cart's Identity Verification (LexisNexis) and Sanctions Screening
 * report PDFs (CHG-0141, Bill 2026-11-02). Two separate reports: either can exist without the other.
 *
 *   GET /v3/risk/carts/{cartId}/lexisnexis-reports      → the cart's people with either report (names only)
 *   GET /v3/identity/extracts/{extractId}/report.pdf     → Identity Verification as a PDF
 *   GET /v3/sanctions/screenings/{screeningId}/report.pdf → Sanctions Screening as a PDF
 *
 * All gate on the per-role report access Risk sets (SuperAdmin always passes). Same inline-fetch,
 * bearer-only pattern as riskReviewApi.ts: no function key in the browser bundle.
 */

import { getMsalToken } from '@/auth/msal';

export interface LexisNexisReportRow {
  personIndex: number;
  displayName: string;
  /** Null when LexisNexis did not run for this person. */
  extractId?: string | null;
  generatedAt?: string | null;
  /** The Identity Verification copy filed in ao_Documents when the cart committed, if any. */
  savedUrl?: string | null;
  /** The person's latest sanctions screening on the cart; null when none ran. */
  screeningId?: string | null;
  screenedAt?: string | null;
  /** clear | review | hit | pending */
  screeningVerdict?: string | null;
  screeningSavedUrl?: string | null;
}

export type ReportKind = 'identity' | 'sanctions';

export interface LexisNexisReportsResponse {
  success: boolean;
  cartId?: string;
  rows?: ReadonlyArray<LexisNexisReportRow>;
  error?: string;
}

function getBaseUrl(): string {
  return (
    (import.meta.env.VITE_API_BASE_URL as string | undefined) ||
    'https://pcb-retail-api.azurewebsites.net/api'
  );
}

async function buildHeaders(accept: string): Promise<Headers> {
  const headers = new Headers({ Accept: accept, 'x-pcb-client-id': 'risk-mgmt-panel' });
  try {
    const token = await getMsalToken();
    if (token) headers.set('Authorization', `Bearer ${token}`);
  } catch {
    // Silent — RoleGate already enforced an authenticated session.
  }
  return headers;
}

async function errorFrom(resp: Response, fallback: string): Promise<Error> {
  try {
    const body = (await resp.json()) as { error?: unknown };
    if (body && typeof body.error === 'string' && body.error) return new Error(body.error);
  } catch {
    // not JSON
  }
  return new Error(`${fallback} (HTTP ${resp.status}).`);
}

export async function listLexisNexisReports(
  cartId: string,
  signal?: AbortSignal,
): Promise<LexisNexisReportsResponse> {
  const url = `${getBaseUrl()}/v3/risk/carts/${encodeURIComponent(cartId)}/lexisnexis-reports`;
  const resp = await fetch(url, {
    method: 'GET',
    headers: await buildHeaders('application/json'),
    ...(signal ? { signal } : {}),
  });
  if (!resp.ok) throw await errorFrom(resp, 'Could not load the reports');
  return (await resp.json()) as LexisNexisReportsResponse;
}

export const REPORT_TITLES: Record<ReportKind, string> = {
  identity: 'Identity Verification',
  sanctions: 'Sanctions Screening',
};

/** Fetches one report as a PDF and returns an object URL the caller opens or downloads (revoke it after). */
export async function fetchReportPdf(kind: ReportKind, id: string): Promise<{ url: string; filename: string }> {
  const path =
    kind === 'identity'
      ? `/v3/identity/extracts/${encodeURIComponent(id)}/report.pdf`
      : `/v3/sanctions/screenings/${encodeURIComponent(id)}/report.pdf`;
  const resp = await fetch(`${getBaseUrl()}${path}`, { method: 'GET', headers: await buildHeaders('application/pdf') });
  if (!resp.ok) throw await errorFrom(resp, `Could not print the ${REPORT_TITLES[kind]} report`);
  const blob = await resp.blob();
  return {
    url: URL.createObjectURL(blob),
    filename: `${REPORT_TITLES[kind]} - ${id}.pdf`,
  };
}
